// src/components/ErrorReports.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  format,
  parseISO,
  subDays,
  addDays,
  isWithinInterval,
} from 'date-fns';

/* ───────────────────────── helpers: CSV + pagination ───────────────────────── */

function downloadCsv({ headers, rows, filename }) {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headerLine = headers.map(h => esc(h.label)).join(',');
  const lines = rows.map(row =>
    headers.map(h => esc(h.get ? h.get(row) : row[h.key])).join(',')
  );
  const csv = [headerLine, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function usePageSlice(items, page, pageSize) {
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return items.slice(start, end);
}

function Pagination({ page, pageSize, total, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < pageCount;

  const wrap = {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderTop: '1px solid #e5e7eb',
    background: '#fff'
  };
  const btn = (enabled) => ({
    padding: '6px 10px',
    border: '1px solid #e5e7eb',
    background: enabled ? '#fff' : '#f3f4f6',
    borderRadius: 8,
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontSize: 12
  });
  const meter = { fontSize: 12, color: '#64748b' };
  const select = {
    padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12
  };

  return (
    <div style={wrap}>
      <span style={meter}>
        Page {page} / {pageCount} • {total.toLocaleString()} rows
      </span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          value={pageSize}
          onChange={(e)=>onChange({ page: 1, pageSize: Number(e.target.value) })}
          style={select}
          title="Rows per page"
        >
          {[10,25,50,100,250,500].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <button onClick={()=>onChange({ page: 1 })} disabled={!canPrev} style={btn(canPrev)} title="First">«</button>
        <button onClick={()=>onChange({ page: page - 1 })} disabled={!canPrev} style={btn(canPrev)} title="Prev">‹</button>
        <button onClick={()=>onChange({ page: page + 1 })} disabled={!canNext} style={btn(canNext)} title="Next">›</button>
        <button onClick={()=>onChange({ page: pageCount })} disabled={!canNext} style={btn(canNext)} title="Last">»</button>
      </div>
    </div>
  );
}

/* ───────────────────────── component ───────────────────────── */

export default function ErrorReports({ reports = [], onRefresh }) {
  // FILTER STATE
  const [emailFilter, setEmailFilter] = useState('');
  const [licenceFilter, setLicenceFilter] = useState('');
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 7), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // PAGINATION STATE
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // bump page back to 1 whenever filters change
  useEffect(() => { setPage(1); }, [emailFilter, licenceFilter, startDate, endDate]);

  // FILTERED
  const filtered = useMemo(() => {
    const s = startDate ? parseISO(startDate) : subDays(new Date(), 3650);
    const e = endDate ? addDays(parseISO(endDate), 1) : addDays(new Date(), 1);

    return reports.filter((r) => {
      const inRange = r.dateUtc && isWithinInterval(new Date(r.dateUtc), { start: s, end: e });
      if (!inRange) return false;

      if (emailFilter && !(r.userEmail || '').toLowerCase().includes(emailFilter.toLowerCase())) {
        return false;
      }
      const licence = r.licenceKey || '';
      if (licenceFilter && !licence.toLowerCase().includes(licenceFilter.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [reports, emailFilter, licenceFilter, startDate, endDate]);

  // SORTED (newest first)
  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const da = a.dateUtc ? new Date(a.dateUtc).getTime() : 0;
      const db = b.dateUtc ? new Date(b.dateUtc).getTime() : 0;
      return db - da;
    });
    return copy;
  }, [filtered]);

  // CURRENT PAGE
  const currentPageRows = usePageSlice(sorted, page, pageSize);

  // CSV headers
  const csvHeaders = [
    { key: 'dateUtc', label: 'Date (Local)', get: r => r.dateUtc ? new Date(r.dateUtc).toLocaleString() : '' },
    { key: 'userEmail', label: 'User Email' },
    { key: 'userAgent', label: 'User Agent' },
    { key: 'licenceKey', label: 'Licence Key' },
    { key: 'errorDetails', label: 'Error Details' },
  ];

  const exportCurrent = () =>
    downloadCsv({ headers: csvHeaders, rows: currentPageRows, filename: `error-reports_page-${page}.csv` });
  const exportAll = () =>
    downloadCsv({ headers: csvHeaders, rows: sorted, filename: `error-reports_all.csv` });

  // local UI styles (kept lightweight and consistent)
  const shell = { background: '#f8fafc', minHeight: '100%', padding: 16 };
  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' };
  const titleRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderBottom: '1px solid #e5e7eb' };
  const filterBar = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: 12, borderBottom: '1px solid #e5e7eb' };
  const input = { padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, outline: 'none' };
  const btnBase = { padding: '8px 12px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  const btnGhost = { ...btnBase, background: '#fff', color: '#0f172a', border: '1px solid #e5e7eb' };
  const btnPrimary = { ...btnBase, background: '#0b0f19', color: '#fff', border: '1px solid #0b0f19' };
  const tableWrap = { overflowX: 'auto' };
  const table = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
  const th = { background: '#f6f7fb', textAlign: 'left', padding: 10, fontWeight: 700, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td = { padding: 10, borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' };

  // headers (for rendering)
  const headers = [
    { key: 'dateUtc', label: 'Date' },
    { key: 'userEmail', label: 'User Email' },
    { key: 'userAgent', label: 'User Agent' },
    { key: 'licenceKey', label: 'Licence Key' },
    { key: 'errorDetails', label: 'Error Details' },
  ];

  return (
    <div style={shell}>
      <div style={card}>
        {/* Title + CSV */}
        <div style={titleRow}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Error Reports</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportCurrent} style={btnGhost}>Export current page</button>
            <button onClick={exportAll} style={btnPrimary}>Export all (filtered)</button>
          </div>
        </div>

        {/* Filters */}
        <div style={filterBar}>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            Start
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ ...input, marginLeft: 6 }}
            />
          </label>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            End
            <input
              type="date"
              value={endDate}
              max={format(new Date(), 'yyyy-MM-dd')}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ ...input, marginLeft: 6 }}
            />
          </label>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            Email
            <input
              type="text"
              placeholder="user@company.com"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              style={{ ...input, marginLeft: 6, minWidth: 220 }}
            />
          </label>
          <label style={{ fontSize: 12, color: '#64748b' }}>
            Licence
            <input
              type="text"
              placeholder="ABC-123…"
              value={licenceFilter}
              onChange={(e) => setLicenceFilter(e.target.value)}
              style={{ ...input, marginLeft: 6, minWidth: 180 }}
            />
          </label>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => typeof onRefresh === 'function' && onRefresh()} style={btnGhost}>Refresh</button>
            <button
              type="button"
              onClick={() => {
                setEmailFilter('');
                setLicenceFilter('');
                setStartDate(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
                setEndDate(format(new Date(), 'yyyy-MM-dd'));
              }}
              style={btnGhost}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                {headers.map(h => (
                  <th key={h.key} style={th}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} style={{ padding: 16, color: '#666' }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                currentPageRows.map((r, i) => (
                  <tr key={`${r.dateUtc || ''}-${i}`} style={{ background: 'transparent' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {r.dateUtc ? new Date(r.dateUtc).toLocaleString() : '—'}
                    </td>
                    <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.userEmail || '—'}
                    </td>
                    <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.userAgent}>
                      {r.userAgent || '—'}
                    </td>
                    <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.licenceKey || '—'}
                    </td>
                    <td style={{ ...td }}>
                      <ExpandableText text={r.errorDetails || ''} maxChars={300} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          page={page}
          pageSize={pageSize}
          total={sorted.length}
          onChange={({ page: p, pageSize: ps }) => {
            if (typeof p === 'number') setPage(p);
            if (typeof ps === 'number') setPageSize(ps);
          }}
        />
      </div>
    </div>
  );
}

/* Expand / collapse long error text */
function ExpandableText({ text, maxChars = 300 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span style={{ color: '#999' }}>—</span>;

  const shouldTruncate = text.length > maxChars;
  const shown = expanded || !shouldTruncate ? text : text.slice(0, maxChars) + '…';

  return (
    <div>
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{shown}</div>
      {shouldTruncate && (
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginTop: 4,
            border: 'none',
            background: 'transparent',
            color: '#0078d4',
            cursor: 'pointer',
            fontSize: 12,
            padding: 0,
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
